import { useState, type FormEvent } from 'react';
import { Key } from '@phosphor-icons/react';
import { RESET_CONFIRMATION, resetTwoFactorInputSchema } from '@mybusinessmate/schema';
import { resetStaffTwoFactor, type StaffHealthRow } from '../../lib/workspace-api';
import { Banner } from '../ui/banner';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Dialog } from '../ui/dialog';
import { Input } from '../ui/input';
import { TextArea } from '../ui/text-area';

function Body({ person, onClose, onDone }: { person: StaffHealthRow; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [signOutEverywhere, setSignOutEverywhere] = useState(true);
  const [errors, setErrors] = useState<{ reason?: string; confirmation?: string }>({});
  const [problem, setProblem] = useState<string>();
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setProblem(undefined);
    const parsed = resetTwoFactorInputSchema.safeParse({ reason, confirmation, signOutEverywhere });
    if (!parsed.success) {
      const next: typeof errors = {};
      for (const issue of parsed.error.issues) next[issue.path[0] as keyof typeof errors] ??= issue.message;
      setErrors(next);
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      await resetStaffTwoFactor(person.id, parsed.data);
      onDone();
    } catch (error) {
      // The server's reason, e.g. "Manage your own two-step in Settings › Security."
      setProblem(
        (error as { message?: string })?.message ?? 'Something went wrong and nothing was changed. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  const first = person.name.split(/\s+/)[0];
  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-(--mbm-space-5)">
      {problem ? <Banner tone="error">{problem}</Banner> : null}
      <TextArea
        variant="filled"
        label="Reason · saved in the audit log"
        rows={3}
        autoFocus
        value={reason}
        error={errors.reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <Input
        variant="filled"
        label={`Type ${RESET_CONFIRMATION} to confirm`}
        autoComplete="off"
        autoCapitalize="characters"
        value={confirmation}
        error={errors.confirmation}
        onChange={(e) => setConfirmation(e.target.value)}
      />
      {person.deviceCount > 0 ? (
        <Checkbox
          checked={signOutEverywhere}
          onChange={(e) => setSignOutEverywhere(e.target.checked)}
          label={`Also sign ${first} out of all ${person.deviceCount} ${person.deviceCount === 1 ? 'device' : 'devices'}`}
        />
      ) : null}
      <div className="grid grid-cols-2 gap-(--mbm-space-3)">
        <Button variant="secondary" size="compact" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button size="compact" type="submit" loading={busy}>
          Reset two-step
        </Button>
      </div>
    </form>
  );
}

/** "Reset two-step for Rahul K.?": a reason and the typed word RESET are required, and the reset is audit-logged (A16.8). */
export function ResetTwoFactorDialog({
  person,
  onClose,
  onDone,
}: {
  person: StaffHealthRow | undefined;
  onClose: () => void;
  onDone: () => void;
}) {
  return (
    <Dialog
      open={Boolean(person)}
      onClose={onClose}
      icon={Key}
      title={person ? `Reset two-step for ${person.name}?` : 'Reset two-step'}
      description="Their authenticator and backup codes stop working now. They’ll set two-step up again at their next sign-in. Only do this once you’ve confirmed it’s really them, on a call or in person."
    >
      {person ? <Body person={person} onClose={onClose} onDone={onDone} /> : null}
    </Dialog>
  );
}
